import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Checkbox, TextField } from '@mui/material';
import { ChangeEvent, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Subject } from 'rxjs';
import { debounceTime, tap } from 'rxjs/operators';
import { PricelistDto } from '../../common/PricePrgClient';
import { RootState } from '../../store';
import { PricelistTreeItem } from './pricelistTypes';
import { getPricelists } from './redux/pricelistSelectors';

export type PricelistTreeProps = {
    multiSelect: boolean;
    pricelistsSelected?: (pricelistId: number | number[]) => void;
    selectedPricelistIds?: number | number[];
    showBasePricelistsOnly?: boolean;
    treeItemRenderer?: (node: PricelistTreeItem) => JSX.Element;
    header?: JSX.Element;
    filter: string;
    setFilter(filter: string): void;
};

const mapStateToProps = (state: RootState) => ({
    pricelists: getPricelists(state)
});

type Props = ReturnType<typeof mapStateToProps> & PricelistTreeProps;

const getPricelistTree = (
    pricelistsObj: { [pricelistId: number]: PricelistDto },
    showBasePricelistsOnly?: boolean
) => {
    let pricelists = Object.values(pricelistsObj);

    if (showBasePricelistsOnly === true) {
        pricelists = pricelists.filter(pl => pl.isBasePricelist);
    }

    const pricelistTreeItems: PricelistTreeItem[] = pricelists
        .map(pl => ({
            id: pl.pricelistId,
            name: pl.name ?? '??',
            ref: pl.pricelistRef,
            displayName: `${pl.pricelistRef} - ${pl.name}`,
            children: [],
            parentId: pl.parentPricelistId,
            currency: pl.currency
        }))
        .sort((a, b) => a.ref.localeCompare(b.ref));

    const map: { [key: number]: number } = {};
    const roots: PricelistTreeItem[] = [];

    for (let i = 0; i < pricelistTreeItems.length; i += 1) {
        map[pricelistTreeItems[i].id] = i;
    }

    for (const pricelist of pricelistTreeItems) {
        if (pricelist.parentId != null) {
            const plTreeItem = pricelistTreeItems[map[pricelist.parentId]];
            if (plTreeItem != null) {
                plTreeItem.children.push(pricelist);
            }
        } else {
            roots.push(pricelist);
        }
    }
    return roots;
};

function getExpandedPricelistIds(
    initialSelectedPricelistIds: number | number[] | undefined,
    pricelists: { [pricelistId: number]: PricelistDto }
) {
    if (initialSelectedPricelistIds == null) {
        return [];
    }

    const selectedPricelistIds = Array.isArray(initialSelectedPricelistIds)
        ? initialSelectedPricelistIds
        : [initialSelectedPricelistIds];

    const expandedPricelistIds = new Set<number>();

    for (const selectedPlId of selectedPricelistIds) {
        const pricelist = pricelists[selectedPlId];
        if (pricelist != null) {
            let parentId = pricelist.parentPricelistId;
            while (parentId != null) {
                expandedPricelistIds.add(parentId);
                parentId = pricelists[parentId]?.parentPricelistId;
            }
        }
    }

    return Array.from(expandedPricelistIds);
}

const pricelistFilterSubject = new Subject<string>();

function filterPricelistTree(pricelistTree: PricelistTreeItem[], filter: string): PricelistTreeItem[] {
    if (filter.length < 3) {
        return pricelistTree;
    }

    return pricelistTree
        .filter(cust => !shouldHidePricelist(cust, filter))
        .map(cust => ({
            ...cust,
            children: filterPricelistTree(cust.children, filter)
        }));
}

function shouldHidePricelist(pricelist: PricelistTreeItem, filter: string): boolean {
    return (
        !(
            pricelist.name.toLowerCase().includes(filter.toLowerCase()) ||
            pricelist.ref.toLowerCase().includes(filter.toLowerCase())
        ) && pricelist.children.every(c => shouldHidePricelist(c, filter))
    );
}

const PricelistTree = ({
    pricelists,
    multiSelect,
    pricelistsSelected,
    selectedPricelistIds,
    treeItemRenderer,
    header,
    showBasePricelistsOnly,
    filter,
    setFilter
}: Props) => {
    const initiallyExpandedPricelistIds = useMemo(
        () => getExpandedPricelistIds(selectedPricelistIds, pricelists),
        [selectedPricelistIds, pricelists]
    );
    const pricelistTree = useMemo(
        () => getPricelistTree(pricelists, showBasePricelistsOnly),
        [pricelists, showBasePricelistsOnly]
    );

    const [expCustIds, setExpCustIds] = useState([] as string[]);
    const [filteredPricelists, setFilteredPricelists] = useState(pricelistTree);

    useEffect(() => {
        setFilteredPricelists(filterPricelistTree(pricelistTree, filter));
    }, [pricelistTree, filter]);

    useEffect(() => {
        const subscription = pricelistFilterSubject
            .pipe(
                tap(cf => setFilter(cf)),
                debounceTime(200)
            )
            .subscribe(cf => setFilteredPricelists(filterPricelistTree(pricelistTree, cf)));

        // Important to unsubscribe on teardown so we don't create a memory leak
        return () => subscription.unsubscribe();
    }, [pricelistTree, setFilter]);

    useEffect(() => {
        if (selectedPricelistIds != null) {
            const isArr = Array.isArray(selectedPricelistIds);
            if (isArr !== multiSelect) {
                throw new Error(
                    'initialSelectedPricelistIds must be of type number[] if multiselect is true, ' +
                        'and of type number if multiselect is set to false.'
                );
            }
        }
    }, [selectedPricelistIds, multiSelect]);

    useEffect(() => {
        setExpCustIds(exp => [...exp, ...initiallyExpandedPricelistIds.map(String)]);
    }, [initiallyExpandedPricelistIds]);

    const handleCheckboxClicked = (pricelistId: number, checked: boolean) => {
        if (pricelistsSelected == null || !Array.isArray(selectedPricelistIds)) {
            return;
        }

        let selectedPlIds: number[];
        if (checked) {
            selectedPlIds = [...selectedPricelistIds, pricelistId];
        } else {
            selectedPlIds = selectedPricelistIds.filter(plId => plId !== pricelistId);
        }

        pricelistsSelected(selectedPlIds.map(Number));
    };

    const isSelected = (pricelistId: number) => {
        if (selectedPricelistIds == null) {
            return false;
        }

        if (Array.isArray(selectedPricelistIds)) {
            return selectedPricelistIds.includes(pricelistId);
        }

        return selectedPricelistIds === pricelistId;
    };

    const renderer = (plItem: PricelistTreeItem) => {
        let label: string | JSX.Element;
        if (treeItemRenderer != null) {
            label = treeItemRenderer(plItem);
        } else {
            if (multiSelect) {
                label = (
                    <Box sx={{ display: 'flex', alignItems: 'center', maxHeight: '25px' }}>
                        <Checkbox
                            checked={isSelected(plItem.id)}
                            onChange={(event, checked) => handleCheckboxClicked(plItem.id, checked)}
                            color="primary"
                        />
                        {plItem.displayName}
                    </Box>
                );
            } else {
                label = plItem.displayName;
            }
        }

        return (
            <TreeItem key={plItem.id} nodeId={String(plItem.id)} label={label}>
                {plItem.children.length > 0 ? plItem.children.map(cust => renderer(cust)) : null}
            </TreeItem>
        );
    };

    const onNodeSelect = useCallback(
        (event: SyntheticEvent<Element, Event>, nodeIds: string | string[]) => {
            if (
                pricelistsSelected == null ||
                event.target instanceof SVGElement ||
                event.target instanceof HTMLInputElement
            ) {
                return;
            }

            const nodeId = Number(nodeIds as string);

            if (!multiSelect) {
                pricelistsSelected(Number(nodeId));
            } else {
                const currSelectedPlIds = selectedPricelistIds as number[] | undefined;
                let newSelectedPlIds: number[];
                if (!(currSelectedPlIds != null)) {
                    newSelectedPlIds = [nodeId];
                } else if (currSelectedPlIds.includes(Number(nodeId))) {
                    newSelectedPlIds = currSelectedPlIds.filter(id => id !== nodeId);
                } else {
                    newSelectedPlIds = [...currSelectedPlIds, nodeId];
                }

                pricelistsSelected(newSelectedPlIds.map(Number));
            }
        },
        [multiSelect, selectedPricelistIds, pricelistsSelected]
    );

    const onNodeToggle = (event: SyntheticEvent<Element, Event>, nodeIds: string[]) => {
        if (event.target instanceof SVGElement) {
            setExpCustIds(nodeIds);
            event.preventDefault();
        }
    };

    const handleOnFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        pricelistFilterSubject.next(event.target.value);
    };

    const getSelected = useCallback(() => {
        if (!Array.isArray(selectedPricelistIds)) {
            return String(selectedPricelistIds) ?? '';
        } else {
            return undefined;
        }
    }, [selectedPricelistIds]);

    return (
        <Box sx={{ height: 'calc(100vh - 48px - 32px)' }}>
            <Box>
                <TextField
                    size="small"
                    variant="standard"
                    sx={{ marginLeft: 2, width: '90%' }}
                    label=""
                    onChange={handleOnFilterChange}
                    value={filter}
                    placeholder="Filter"
                />
            </Box>

            {header != null && <Box>{header}</Box>}
            <Box sx={{ marginTop: header != null ? undefined : '16px' }}>
                <TreeView
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={expCustIds}
                    selected={getSelected()}
                    onNodeToggle={onNodeToggle}
                    onNodeSelect={onNodeSelect}
                >
                    {filteredPricelists.map(cust => renderer(cust))}
                </TreeView>
            </Box>
        </Box>
    );
};

export default connect(mapStateToProps)(PricelistTree);
